/**
 * Deriving a full accent swatch from a practice's own arbitrary brand colour (`BACKLOG.md` B4's "org
 * brand" item, `claude-kit/rules/invariants.md`'s "Commercial products let the customer brand it").
 *
 * `palette.ts`'s `ACCENT_COLORS` is a *closed* enum — six names, each with every one of `solid`/
 * `onSolid`/`text`/`soft` hand-picked and pre-verified in `palette.test.ts`. A practice's own brand
 * hex cannot go through that table at all: it is one arbitrary value, not a name, so the other three
 * values (a legible ink, a tinted background) have to be *derived* from it instead of looked up.
 *
 * This is the consumer `contrast.ts`'s own header comment names as "not consumed by anything yet" —
 * `betterForeground` was written and tested specifically for "the day an arbitrary hex reaches it,"
 * and this module is that day.
 *
 * Deliberately pure and DOM-free, matching `palette.ts`/`contrast.ts`'s own shape: `ThemeProvider.tsx`
 * is the one place a derived swatch is actually written to the document, exactly as it is the one
 * place `themeAttributes`'s output is.
 */

import { betterForeground, parseColor } from './contrast.ts';

/** The two ink poles `deriveOrgAccentOverride` chooses between for `onSolid` — matching
 * `contrast.test.ts`'s own regression fixture (`#14181f`) and `palette.ts`'s `NOTICE.light.infoBg`,
 * so a practice's brand colour and this app's own "high-contrast dark ink" land on the identical
 * near-black rather than a second, slightly different one invented here. */
const WHITE_INK = '#ffffff';
const NEAR_BLACK_INK = '#14181f';

export interface OrgAccentOverride {
  /** Filled control background — the practice's own colour, verbatim. */
  readonly solid: string;
  /** Text/icons drawn on `solid` — whichever of `WHITE_INK`/`NEAR_BLACK_INK` actually measures
   * better against it, per `betterForeground`, never a luminance-threshold guess. */
  readonly onSolid: string;
  /** The practice's colour used as text or an icon directly on a page surface. Also the practice's
   * own colour, verbatim — `web/src/theme/guard.ts`'s `verifyTheme` is what actually confirms this
   * clears AA against both theme modes' surfaces once applied; a colour that fails is rejected and
   * rolled back there, not silently adjusted here. */
  readonly text: string;
  /**
   * A tinted block — active navigation, selected rows — expressed as a live `color-mix()` against
   * `var(--s-surface)` rather than a pre-computed hex. Two reasons: it needs no light/dark branch of
   * its own (`--s-surface` already resolves to the right one), and it is not a text pairing
   * `REQUIRED_PAIRS` in `guard.ts` checks at all — a *background* tint has no AA requirement the way
   * `text`/`onSolid` do, so there is nothing here for a contrast check to verify or reject.
   */
  readonly soft: string;
}

/**
 * Derive a full swatch from a practice's brand colour, or `null` if it does not parse as a colour
 * `guard.ts`'s runtime check could ever measure (matching `contrast.ts`'s own "never guess" rule:
 * an unreadable value is a failure, not a fallback the caller might mistake for the product default).
 *
 * `soft`'s `color-mix()` string is intentionally environment-dependent (its actual rendered colour
 * depends on `--s-surface`, resolved by the browser at paint time) — `guard.ts` never checks it, so
 * this is safe; nothing in this codebase treats `soft` as a value that must independently clear AA.
 */
export function deriveOrgAccentOverride(brandColor: string): OrgAccentOverride | null {
  if (parseColor(brandColor) === null) return null;

  const onSolid = betterForeground(brandColor, WHITE_INK, NEAR_BLACK_INK);
  if (onSolid === null) return null;

  return {
    solid: brandColor,
    onSolid: onSolid.color,
    text: brandColor,
    soft: `color-mix(in srgb, ${brandColor} 18%, var(--s-surface))`,
  };
}
